import { NotificationsProps } from "../types";
import { Divider, Drawer, Typography } from "antd";
import { INotification, NotificationStatus } from "../common/types";
import { CheckIcon, EllipsisHorizontalIcon, InformationCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../store";
import { getAndReadNotifications } from "../slices/appInfoSlice";

const { Text, Title } = Typography;

const NotificationsPanel = ({ open, onClose: onClose }: NotificationsProps) => {
  if (!open) {
    return;
  }

  const dispatch = useDispatch<AppDispatch>();
  const notifications = dispatch(getAndReadNotifications());

  const renderNotificationIcon = (status: NotificationStatus) => {
    const commonClasses = "w-4 h-4 inline-block mr-1";
    const statusIcons = {
      [NotificationStatus.InProgress]: <EllipsisHorizontalIcon className={`${commonClasses} text-blue-600`} />,
      [NotificationStatus.Success]: <CheckIcon className={`${commonClasses} text-green-600`} />,
      [NotificationStatus.Warning]: <Text className="text-yellow-400">&nbsp;!&nbsp;&nbsp;</Text>,
      [NotificationStatus.Info]: <InformationCircleIcon className={`${commonClasses} text-blue-600`} />,
      [NotificationStatus.Error]: <XMarkIcon className={`${commonClasses} text-red-600`} />,
    };
  
    return statusIcons[status] || null;
  };

  const handleClick = (notification: INotification) => {
    if (!notification.linkConfig) return;
    
    if (notification.linkConfig.onClick) {
      try {
        notification.linkConfig.onClick();
      } catch (error) {
        console.error("Error in notification panel click handler:", error);
        setTimeout(() => {
          try {
            if (notification.linkConfig?.onClick) notification.linkConfig.onClick();
          } catch (retryError) {
            console.error("Error in notification panel retry handler:", retryError);
            window.openNotification(
              NotificationStatus.Error,
              "Action Failed",
              "Could not complete the requested action. Please try again.",
              undefined,
              false
            );
          }
        }, 500);
      }
    } else if (notification.linkConfig.url) {
      window.open(notification.linkConfig.url, "_blank");
    }
  };

  const renderMessage = (notification: INotification) => {
    if (notification.linkConfig) {
      return (
        <Text
          className="cursor-pointer text-blue-500 hover:underline"
          onClick={() => handleClick(notification)}
        >
          {notification.message}
        </Text>
      );
    }
    return <Text>{notification.message}</Text>;
  };

  return (
    <Drawer
      title={
        <Title level={4}>Notifications</Title>
      }
      mask={false}
      open={open}
      rootStyle={{ top: 65 }}
      onClose={onClose}
    >
      {notifications.length === 0 && (
        <Text>No notifications found.</Text>
      )}

      {notifications.slice(0).reverse().map((notification: INotification, index: number) => (
        <div key={index}>
          <Title level={5}>
            {renderNotificationIcon(notification.status)}
            {notification.title}
          </Title>
          {renderMessage(notification)}
          <Divider />
        </div>
      ))}
    </Drawer>
  );
};

export default NotificationsPanel;